import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import Checkbox from "@cayuse/shared-react.components.form.inputs.checkbox";
import IconButton from "@cayuse/shared-react.components.icon-button";
import { crunchClasses } from "@cayuse/shared-react.utils.helpers";
import { isNonEmptyArray, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";

import { EditableTableCell } from "components/BudgetBuilder/shared/EditableTableCell";
import { useTenantFormatter } from "components/BudgetBuilder/shared/TenantFormatter";
import OrgSearchInput from "components/OrgSearchInput";
import Table from "components/Table";
import { picklistsSelectors, tenantSelectors, budgetBuilderSelectors } from "store/selectors";
import { fetchSingleOrganization } from "store/actions/picklists";
import { formatNumberAsPercent, getSubawardCapSpaceForPeriod, toNumber } from "utils/budgetBuilder";

import styles from "./styles.css";


const SubawardLineItemsRow = (
	{
		item,
		categoriesByPeriod,
		isEditing,
		handleRemoveItem,
		updateLineItem,
	},
) => {
	const { subawardCostCap } = useSelector(tenantSelectors.tenant);
	const { currencyFormatter } = useTenantFormatter();
	const currentPeriodNumber = useSelector(budgetBuilderSelectors.currentPeriodNumber);
	let { hash, value, used, locked, isFA, FA, directCost, isExpanded } = item;

	const currentPeriodRate = useSelector(budgetBuilderSelectors.currentPeriodRate);
	const { options: organizations } = useSelector(picklistsSelectors.organizations);

	const classNames = crunchClasses({
		[styles.row]: true,
		[styles.hiddenRow]: !isExpanded,
	});

	const name = useMemo(
		() => organizations.find(org => org.id === value)?.text,
		[ organizations, value ],
	);

	const currentCapSpace = useMemo(
		() => getSubawardCapSpaceForPeriod(categoriesByPeriod, subawardCostCap, currentPeriodNumber),
		[ categoriesByPeriod, currentPeriodNumber, subawardCostCap ],
	);

	const faCost = useMemo(() => {
		if (isFA && directCost) {
			const rate = toNumber(FA) || currentPeriodRate;
			const cost = toNumber(directCost) > currentCapSpace ? currentCapSpace : toNumber(directCost);

			return cost * rate / 100;
		}

		return 0;
	}, [ FA, currentCapSpace, currentPeriodRate, directCost, isFA ]);

	const totalCost = useMemo(
		() => toNumber(directCost) + toNumber(faCost),
		[ directCost, faCost ],
	);

	useEffect(() => {
		if (isNonEmptyString(value) && isNonEmptyArray(organizations)) {
			if (!organizations.find(u => u.value === value)) {
				fetchSingleOrganization(value);
			}
		}
	}, [ value, organizations ]);


	const onOrgFinderChange = value => updateLineItem({ value });

	const onRemove = () => {
		handleRemoveItem(value);
	};

	return (
		<Table.Row
			key={hash}
			id={hash}
			className={classNames}
		>
			<Table.Cell className={`${styles.expandCell} ${styles.orgFinderCell}`}>
				{!isEditing
					? <span>{name}</span>
					: (
						<OrgSearchInput
							onChange={onOrgFinderChange}
							value={value}
							className={styles.orgFinderDropdown}
						/>
					)
				}
			</Table.Cell>
			<EditableTableCell
				editable={isEditing}
				cellStyles={`${styles.cellAlignRight} ${styles.valueCellPadding}`}
				textFormatter={currencyFormatter}
				value={directCost}
				onChange={event => updateLineItem({ directCost: event.target.value })}
			/>
			<Table.Cell className={styles.cellAlignRight}>
				<Checkbox
					checked={isFA}
					disabled={!isEditing}
					onClick={() => updateLineItem({ isFA: !isFA })}
				/>
			</Table.Cell>
			<Table.Cell className={`${styles.cellAlignRight} ${styles.valueCellPadding}`}>
				<span className={styles.cellAlignRight}>
					{formatNumberAsPercent(currentPeriodRate, "0%")}
				</span>
			</Table.Cell>
			<Table.Cell className={`${styles.cellAlignRight} ${styles.valueCellPadding}`}>
				<span className={`${styles.cellAlignRight} ${styles.bold}`}>
					{currencyFormatter(faCost)}
				</span>
			</Table.Cell>
			<Table.Cell className={`${styles.cellAlignRight} ${styles.valueCellPadding}`}>
				<span className={`${styles.cellAlignRight} ${styles.bold}`}>
					{currencyFormatter(totalCost)}
				</span>
			</Table.Cell>
			<Table.Cell className={styles.cellAlignCenter}>
				{(!used || !locked) && isEditing && (
					<IconButton
						className={`${styles.rowIcon} ${styles.red}`}
						onClick={onRemove}
						size="bigger"
						icon="times"
						tertiary
					/>
				)}
			</Table.Cell>
		</Table.Row>
	);
};

export default SubawardLineItemsRow;
